// Shared guards for rendering `conversation.metadata` in the transcripts UI.
//
// conversation.metadata is written by many producers (survey panels,
// screeners, and the translation pipeline's per-language bookkeeping) and is
// NOT guaranteed to be primitive. The pipeline stores nested objects under
// `translation_failures` and `translation_enqueued_at`. An object reaching a
// React child throws React #31 ("Objects are not valid as a React child")
// and crashes the whole tab; even where it's wrapped in String() it leaks
// "[object Object]" to the researcher. These two helpers are the single
// source of truth used by every metadata render site (table columns and the
// single-transcript detail view) so the guard can never drift between them.

/**
 * Internal bookkeeping keys that must never surface as a metadata column or
 * chip — object-valued and meaningless to researchers. This is the cosmetic
 * "don't show it" layer; `formatMetadataCellValue` is the systemic guard
 * that stops ANY object-valued value (including a future key not listed
 * here) from crashing the UI.
 */
export const HIDDEN_METADATA_KEYS = new Set([
  'computed_quality',
  'translation_failures',
  'translation_enqueued_at',
]);

/**
 * Coerce a metadata value to a safe display string. Objects/arrays render as
 * the '/' placeholder so they can never reach a React child. Only genuinely
 * empty values (null / undefined / '') render as '/' — meaningful falsy
 * primitives like 0 or false display as themselves (a panel can legitimately
 * send participant_id=0 or a "false" screener answer).
 */
export const formatMetadataCellValue = (value: unknown): string => {
  if (typeof value === 'object' && value !== null) return '/';
  if (value === null || value === undefined || value === '') return '/';
  if (typeof value === 'number' && Number.isNaN(value)) return '/';
  return String(value);
};

/**
 * TELV1-506 — whether a metadata value is a renderable primitive. Object /
 * array values are internal bookkeeping (the translation pipeline's
 * per-language records) and are hidden STRUCTURALLY: a key seen with an
 * object value in ANY conversation is dropped from the column set and the
 * detail-view chips, so a new bookkeeping key never has to be added to
 * HIDDEN_METADATA_KEYS by hand again. `null` is renderable — it coerces to
 * the '/' placeholder rather than hiding the whole column.
 */
export const isRenderableMetadataValue = (value: unknown): boolean =>
  value === null || typeof value !== 'object';
